import { Fragment, useState } from "react";
import { AccordionControl } from "shared/app/ExplainRecommendationModal/components/AccordionControl";
import {
  fieldAccountTypeMap,
  fieldToBase,
  fieldTypeOperatorOptions,
  fieldsWithAccountType,
  GROUP_ROLE_LABELS
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/constants";
import {
  FieldName,
  FieldType,
  GroupRole,
  Rule
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { Application, Reasons } from "types/application";
import { getFullName } from "shared/utils/get-full-name";
import { formatTitleFirstWord } from "utils/formatTitle";
import FailedIcon from "shared/icons/status-icons/fail-icon.svg?react";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import { cx } from "shared/utils";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import { VerticalSpacer } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/components/VerticalSpacer";
import {
  formatApplicantValue,
  formatConditionSubTitle,
  formatConditionTitle,
  formatThreshold
} from "shared/app/ExplainRecommendationModal/utils";
import { ApplicantType } from "shared/types";
import { normalizeRuleGroupRoles } from "shared/app/VerificationTemplate/helpers";

const formatAccountTypeLabel = (fieldName: FieldName): string => {
  const baseField = fieldToBase[fieldName];
  if (!baseField) return "All accounts";
  const variants = fieldAccountTypeMap[baseField] ?? {};
  const accountType = Object.keys(variants).find(
    (key) => variants[key] === fieldName
  );
  return accountType === "rental" ? "Rental account" : "All accounts";
};

/** Format group_roles for display: "All roles" or "Applicants, Guarantors" etc. */
const formatGroupRolesLabel = (roles: GroupRole[]): string => {
  if (roles.length === 0 || roles.includes(GroupRole.All))
    return GROUP_ROLE_LABELS[GroupRole.All];
  return roles.map((r) => `${GROUP_ROLE_LABELS[r]}s`).join(", ");
};

const ROLE_MAP: Record<GroupRole, ApplicantType | null> = {
  [GroupRole.All]: null,
  [GroupRole.Applicant]: ApplicantType.Applicant,
  [GroupRole.CoSigner]: ApplicantType.CoSigner,
  [GroupRole.Occupant]: ApplicantType.Occupant
};

const matchesGroupRoles = (
  applicantRole: ApplicantType | null | undefined,
  ruleRoles: GroupRole[]
) => {
  if (ruleRoles.includes(GroupRole.All)) return true;
  if (!applicantRole) return false;
  return ruleRoles.some(
    (r) => r !== GroupRole.All && applicantRole === ROLE_MAP[r]
  );
};

interface AccordionProps {
  rule: Rule;
  ruleSet: Reasons;
  application: Application;
  idx?: number;
}

export const Accordion = ({
  rule,
  ruleSet,
  application,
  idx = 0
}: AccordionProps) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState<{
    [key: string]: boolean;
  }>({});
  const isSkipped = Boolean(rule.skipped);
  const roleValues = normalizeRuleGroupRoles(rule);
  const roleLabel = formatGroupRolesLabel(roleValues);
  const formattedFieldName = formatConditionTitle(rule.field_name);
  const formattedFieldNSubtitle = formatConditionSubTitle(rule.field_name);

  const detailContent = () => {
    if (isSkipped)
      return (
        <p className="text-xs font-medium text-content-300">
          No applicants with the required role.
        </p>
      );

    if (rule.per_applicant_results) {
      const rows = rule.per_applicant_results
        .map((applicant) => {
          const applicantData = application.applicants.find(
            (el) => el.id === applicant.applicant_id
          );

          if (
            !applicantData ||
            !matchesGroupRoles(applicantData.role, roleValues)
          )
            return null;

          return (
            <div
              key={applicant.applicant_id}
              className="flex w-full justify-between gap-4 text-xs text-content-1000 font-medium"
            >
              <p>
                {getFullName({
                  firstName: applicantData.first_name,
                  lastName: applicantData.last_name,
                  middleName: applicantData.middle_name,
                  short: true
                })}
              </p>

              <div className="flex gap-3 items-center">
                <p>{formatApplicantValue(rule, applicant.value)}</p>
                {applicant.result ? (
                  <SuccessIcon className="w-4 shrink-0 text-primary-500" />
                ) : (
                  <FailedIcon className="w-4 shrink-0 text-content-100" />
                )}
              </div>
            </div>
          );
        })
        .filter(Boolean);

      if (rows.length > 0) return <div className="space-y-4">{rows}</div>;
    }

    return (
      <div className="flex w-full justify-between gap-4 text-xs text-content-1000 font-medium">
        <p>
          Group {rule.field_type}{" "}
          {formatTitleFirstWord(rule.field_name).toLocaleLowerCase()}
        </p>

        <div className="flex gap-3 items-center">
          <p>{rule.computed_value}</p>
          {rule.result ? (
            <SuccessIcon className="w-4 shrink-0 text-primary-500" />
          ) : (
            <FailedIcon className="w-4 shrink-0 text-content-100" />
          )}
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <Show if={idx !== 0}>
        <div>
          <VerticalSpacer height={16} className="ml-6" />
          <Badge variant={BadgeVariant.BASE} size={BadgeSize.BASE}>
            AND
          </Badge>
          <VerticalSpacer height={16} className="ml-6" />
        </div>
      </Show>

      <div className="flex w-full flex-col">
        <div
          className={cx(
            "flex w-full border border-alpha-100 items-stretch",
            isAccordionOpen[ruleSet.rule_set_id] ? "rounded-t-lg" : "rounded-lg"
          )}
        >
          <div
            className={cx(
              "max-w-full w-full bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100 flex items-center",
              isAccordionOpen[ruleSet.rule_set_id]
                ? "rounded-tl-lg"
                : "rounded-l-lg"
            )}
          >
            <p className="text-content-1000 font-medium text-sm truncate">
              {formattedFieldName}
              <Show if={formattedFieldNSubtitle}>
                <span className="text-content-300">
                  {" " + formattedFieldNSubtitle}
                </span>
              </Show>
            </p>
          </div>
          <Show if={rule.field_type}>
            <div className="w-full max-w-[135px] bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100 flex items-center">
              <p className="text-content-1000 font-medium text-sm">
                {formatTitleFirstWord(rule.field_type || FieldType.Any)}
              </p>
            </div>
          </Show>
          {/* After the migration all templates should have a group_type, so Show condition can be removed after */}
          <Show if={rule.group_type}>
            <div className="w-full max-w-[145px] bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100 flex items-center">
              <p className="text-content-1000 font-medium text-sm">
                {formatTitleFirstWord(rule.group_type || "No Grouping")}
              </p>
            </div>
          </Show>
          <div className="w-full max-w-[150px] bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100 flex items-center">
            <p className="text-content-1000 font-medium text-sm">{roleLabel}</p>
          </div>
          <Show
            if={fieldsWithAccountType.includes(
              fieldToBase[rule.field_name] ?? rule.field_name
            )}
          >
            <div className="w-full max-w-[135px] bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100 flex items-center">
              <p className="text-content-1000 font-medium text-sm">
                {formatAccountTypeLabel(rule.field_name)}
              </p>
            </div>
          </Show>
          <div className="w-full max-w-[48px] bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100 flex items-center">
            <p className="text-content-1000 font-medium text-sm">
              {
                fieldTypeOperatorOptions.find(
                  (el) => el.value === rule.operator
                )?.label
              }
            </p>
          </div>
          <div className="w-fit bg-content-1000-inverted py-3 px-3.5 border-r border-alpha-100 flex items-center">
            <p className="text-content-1000 font-medium text-sm">
              {formatThreshold(rule)}
            </p>
          </div>
          <AccordionControl
            itemResult={isSkipped ? undefined : rule.result}
            itemId={ruleSet.rule_set_id}
            isAccordionOpen={isAccordionOpen}
            setIsAccordionOpen={setIsAccordionOpen}
          />
        </div>

        {isAccordionOpen[ruleSet.rule_set_id] && (
          <div className="bg-background-1000 p-6 space-y-4 rounded-b-lg border-x border-b border-alpha-100">
            {detailContent()}
          </div>
        )}
      </div>
    </Fragment>
  );
};
